// src/pages/Expedientes/VinculadosTab.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Snackbar, Tooltip, TextField, Typography, TablePagination,
    Checkbox, FormControlLabel, Chip, InputAdornment
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MuiAlert from '@mui/material/Alert';
import { useDropzone } from "react-dropzone";
import Autocomplete from '@mui/material/Autocomplete';
import {
    expedienteVinculadosJWT, crearVinculadoJWT, eliminarVinculadoJWT,
    downloadVinculadoJWT, getCombosJWT,
    expedientesBusquedaUnificadaJWT
} from "../../api/endpoints";
import { nodeApiPath } from "../../utils/paths";
import usePermisosModulo from "../../hooks/usePermisosModulo";

function normalizeKeys(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}

function AlertSnackbar(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// ---------- Estilos: ancho/scroll + botones consistentes ----------
const useStyles = makeStyles((theme) => ({
    root: { width: "100%" },
    tableWrap: {
        width: "100%",
        overflowX: "auto",
        borderRadius: 4,
        "& table": { minWidth: 960 },
        [theme.breakpoints.down('lg')]: { "& table": { minWidth: 900 } },
        [theme.breakpoints.down('md')]: { "& table": { minWidth: 760 } },
        [theme.breakpoints.down('sm')]: { "& table": { minWidth: 640 } },
    },
    btnPrimary: {
        borderRadius: 4, background: "#1567B7", color: "#fff",
        fontWeight: 700, textTransform: "none",
    },
    btnDangerOutlined: {
        borderRadius: 4, borderColor: "#F22E44", color: "#F22E44",
        fontWeight: 600, textTransform: "none",
    },
    // NUEVO: fila clickeable + feedback
    rowClickable: {
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(21,103,183,0.06)" }
    },
    titleActions: {
        position: "absolute",
        right: 8,
        top: 8,
        display: "flex",
        gap: 6,
    },
}));

// --- Helpers de tipo ---

export function getIconByTipoArchivo(tipo_archivo = "", extension = "") {
    const t = (tipo_archivo || "").toLowerCase();
    switch (t) {
        case "ms-word": return <DescriptionIcon style={{ color: "#1567B7" }} />;
        case "ms-excel": return <TableChartIcon style={{ color: "#43a047" }} />;
        case "adobe pdf": return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />;
        case "imagen": return <ImageIcon style={{ color: "#8e24aa" }} />;
        case "expediente": return <LinkIcon style={{ color: "#1567B7" }} />;
        case "coordenadas": return <InsertDriveFileIcon style={{ color: "#789262" }} />;
        case "base 64": return <InsertDriveFileIcon style={{ color: "#789262" }} />;
        default:
            if (extension) {
                switch ((extension || "").toLowerCase()) {
                    case "pdf": return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />;
                    case "doc":
                    case "docx": return <DescriptionIcon style={{ color: "#1567B7" }} />;
                    case "xls":
                    case "xlsx": return <TableChartIcon style={{ color: "#43a047" }} />;
                    case "jpg":
                    case "jpeg":
                    case "png":
                    case "gif": return <ImageIcon style={{ color: "#8e24aa" }} />;
                    default: return <InsertDriveFileIcon style={{ color: "#6B89AC" }} />;
                }
            }
            return <InsertDriveFileIcon style={{ color: "#6B89AC" }} />;
    }
}

const formatDateTime24 = (isoOrDate) => {
    if (!isoOrDate) return "";
    // Si ya viene formateado desde el backend (dd/MM/yyyy HH:mm), usarlo directo
    if (typeof isoOrDate === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(isoOrDate)) return isoOrDate;
    const d = new Date(isoOrDate);
    if (isNaN(d.getTime())) return String(isoOrDate);
    const fecha = d.toLocaleDateString("es-AR");
    const hora = d.toLocaleTimeString("es-AR", { hour12: false, hour: "2-digit", minute: "2-digit" });
    return `${fecha}, ${hora}`;
};

// URL para download via el backend Node.js
const fileUrlOf = (v, idExpediente) =>
    v?.id ? nodeApiPath(`expedientes/${idExpediente}/vinculados/${v.id}/download`) : "";



async function existsFile(url) {
    if (!url) return false;
    try {
        const token = localStorage.getItem("SIGIF_JWT_TOKEN");
        const resp = await fetch(url, {
            method: "HEAD",
            headers: token ? { "Authorization": `Bearer ${token}` } : {},
        });
        return resp.ok;
    } catch {
        return false;
    }
}

// Construir URL de descarga con token en query param (para <iframe>, <img>, window.open)
function authFileUrl(idExpediente, idVinculado) {
    const token = localStorage.getItem("SIGIF_JWT_TOKEN");
    const base = nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinculado}/download`);
    return token ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : base;
}

export const extensionesPorTipo = {
    "ms-word": ["doc", "docx"],
    "ms-excel": ["xls", "xlsx"],
    "adobe pdf": ["pdf"],
    imagen: ["jpg", "jpeg", "png", "gif"],
    coordenadas: ["kml", "kmz"],
    "base 64": ["txt"],
};

export function getTipoLabel(tipo_archivo = "") {
    switch ((tipo_archivo || "").toLowerCase()) {
        case "ms-word": return "Word";
        case "ms-excel": return "Excel";
        case "adobe pdf": return "PDF";
        case "imagen": return "Imagen";
        case "expediente": return "Expediente";
        case "coordenadas": return "Coordenadas";
        case "base 64": return "Base 64";
        default: return tipo_archivo || "";
    }
}

function ArchivoViewer({ url, ext }) {
    if (!url || !ext) return null;
    const lowerExt = ext.toLowerCase();
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(lowerExt)) {
        const viewUrl = "https://view.officeapps.live.com/op/embed.aspx?src=" + encodeURIComponent(url);
        return <iframe src={viewUrl} style={{ width: 800, height: 500, border: "none" }} title="Office Viewer" />;
    }
    if (lowerExt === "pdf") {
        return (
            <object data={url} type="application/pdf" width="800" height="500">
                <a href={url} target="_blank" rel="noopener noreferrer">Ver PDF</a>
            </object>
        );
    }
    if (["jpg", "jpeg", "png", "gif"].includes(lowerExt)) {
        return <img src={url} alt="imagen" style={{ maxWidth: 600, maxHeight: 400, margin: 18 }} />;
    }
    return <a href={url} target="_blank" rel="noopener noreferrer">Descargar archivo</a>;
}

export default function VinculadosTab({ idExpediente, permisos = { puedeAgregar: true, puedeEliminar: true }, onChanged, modulo }) {
    const classes = useStyles();

    // --- ESTADOS PRINCIPALES ---
    const [vinculados, setVinculados] = useState([]);

    const [notFoundOpen, setNotFoundOpen] = useState(false);

    // 🔎 Búsqueda
    const [q, setQ] = useState("");

    // 👉 Declarar esto ANTES de usarlo en rowToDelete
    const [idToDelete, setIdToDelete] = useState(null);

    // Lista filtrada (por descripción o tipo)
    const filtered = useMemo(() => {
        const n = (s) =>
            String(s || "")
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                .toLowerCase().trim();

        return (vinculados || []).filter(v =>
            n(v.descripcion).includes(n(q)) ||
            n(getTipoLabel(v.tipo_archivo)).includes(n(q))
        );
    }, [vinculados, q]);

    const closePreview = () => {
        setOpenPreview(false);
        if (previewData?.blob && previewData?.url) {
            try { URL.revokeObjectURL(previewData.url); } catch { }
        }
        setPreviewData({});
    };

    // Fila seleccionada para eliminar (ya podés usar idToDelete sin crash)
    const rowToDelete = useMemo(
        () => (vinculados || []).find(x => x.id === idToDelete) || null,
        [vinculados, idToDelete]
    );

    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [openConfirm, setOpenConfirm] = useState(false);

    // Paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Modal alta
    const [openAlta, setOpenAlta] = useState(false);
    const [descripcion, setDescripcion] = useState("");
    const [tipoArchivo, setTipoArchivo] = useState(null);
    const [tiposArchivo, setTiposArchivo] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [archivoViewer, setArchivoViewer] = useState(null); // { url, ext }

    // Checkbox "Generar movimiento"
    const [generaMov, setGeneraMov] = useState(false);

    // Autocomplete Expedientes
    const [expBuscar, setExpBuscar] = useState("");
    const [expTipoFiltro, setExpTipoFiltro] = useState("todos"); // "todos", "judicial", "admin"
    const [expedientes, setExpedientes] = useState([]);
    const [selectedExp, setSelectedExp] = useState(null);
    const [loadingExp, setLoadingExp] = useState(false);

    // Modal vista previa
    const [openPreview, setOpenPreview] = useState(false);
    const [previewData, setPreviewData] = useState({});

    // ---------------- Permisos por módulo ----------------
    const { permDe, has: hasPerm, ready: permReady } = usePermisosModulo(modulo);
    const permVinc = permDe("PESTAÑA VINCULADOS", "VINCULADOS");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const perm = useMemo(() => ({
        ready: permReady,
        select: permVinc.select,
        insert: permVinc.insert || hasPerm("VINCULAR DOCUMENTO", "INSERT"),
        delete: permVinc.delete
    }), [permReady, permVinc, hasPerm]);

    // Reset form al cerrar modal
    const resetForm = () => {
        setDescripcion("");
        setTipoArchivo(null);
        setSelectedFile(null);
        setFilePreview(null);
        setSelectedExp(null);
        setExpTipoFiltro("todos");
        setGeneraMov(false);
    };

    // Tipos de archivo (filtrados y ordenados)
    const TIPOS_OCULTOS = ["base 64", "biblioteca"];
    const TIPOS_ORDEN = ["expediente", "ms-word", "ms-excel", "adobe pdf", "imagen", "coordenadas"];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        getCombosJWT('tipos_archivo')
            .then(data => {
                const raw = (data?.items || [])
                    .filter(t => t.id !== 0 && t.label && t.label.trim() !== "")
                    .map(t => ({
                        id: t.id,
                        tipo_archivo: t.tipo_archivo || t.label,
                        extension: t.extension || '',
                    }))
                    .filter(t => !TIPOS_OCULTOS.includes((t.tipo_archivo || "").toLowerCase()));
                // Ordenar según TIPOS_ORDEN
                raw.sort((a, b) => {
                    const ia = TIPOS_ORDEN.indexOf((a.tipo_archivo || "").toLowerCase());
                    const ib = TIPOS_ORDEN.indexOf((b.tipo_archivo || "").toLowerCase());
                    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                });
                setTiposArchivo(raw);
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Traer vinculados
    const fetchVinculados = useCallback(() => {
        if (perm.ready && !perm.select) { setLoading(false); return; }
        setLoading(true);
        expedienteVinculadosJWT(idExpediente)
            .then(data => {
                if (data?.ok) {
                    setVinculados((data.vinculados || []).map(normalizeKeys));
                }
            })
            .catch(err => {
                setSnackbar({ open: true, message: "Error al cargar vinculados.", severity: "error" });
            })
            .finally(() => setLoading(false));
    }, [idExpediente, perm.ready, perm.select]);

    useEffect(() => { if (perm.ready) fetchVinculados(); }, [perm.ready, fetchVinculados]);

    // Autocomplete expedientes AJAX (filtrado por tipo server-side)
    useEffect(() => {
        if (!expBuscar || expBuscar.length < 2) { setExpedientes([]); return; }
        setLoadingExp(true);
        const params = { q: expBuscar, limit: 30 };
        if (expTipoFiltro === "judicial") params.tipo = 1;
        else if (expTipoFiltro === "admin") params.tipo = 2;
        expedientesBusquedaUnificadaJWT(params)
            .then(data => {
                setExpedientes((data?.expedientes || []).map(normalizeKeys));
            })
            .catch(() => { })
            .finally(() => setLoadingExp(false));
    }, [expBuscar, expTipoFiltro]);

    // Dropzone (top-level)
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles && acceptedFiles[0]) {
            const f = acceptedFiles[0];
            setSelectedFile(f);
            if (f.type.startsWith("image/") || f.type === "application/pdf") {
                setFilePreview(URL.createObjectURL(f));
            } else {
                setFilePreview(null);
            }
        }
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    // Eliminar archivo
    const handleAskDelete = (id) => { if (!perm.delete) return; setIdToDelete(id); setOpenConfirm(true); };
    const handleConfirmDelete = async () => {
        setOpenConfirm(false);
        try {
            const r = await eliminarVinculadoJWT(idExpediente, idToDelete);
            if (r?.ok) {
                setSnackbar({ open: true, message: "Vinculado eliminado.", severity: "success" });
                fetchVinculados();
                if (onChanged) onChanged();
            } else {
                setSnackbar({ open: true, message: r?.mensaje || "Error al eliminar.", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.message || "Error al eliminar.", severity: "error" });
        }
        setIdToDelete(null);
    };

    // Guardar nuevo vinculado
    const handleGuardar = async () => {
        if (!perm.insert) return;
        setUploading(true);
        try {
            const datos = {
                id_tipo_archivo: tipoArchivo ? tipoArchivo.id : "",
                descripcion: descripcion,
                genera_mov: generaMov ? "1" : "0",
            };
            if (tipoArchivo && tipoArchivo.tipo_archivo.toLowerCase() === "expediente" && selectedExp) {
                // Guardar tipo:id para saber si es judicial o admin al abrir
                const rawTipo = (selectedExp.tipo || "").toLowerCase();
                const expTipo = rawTipo.startsWith("admin") ? "admin" : "judicial";
                datos.url_vinculado = `${expTipo}:${selectedExp.id}`;
                if (!datos.descripcion) {
                    const esAdmin = rawTipo.startsWith("admin");
                    const detalle = esAdmin
                        ? (selectedExp.extracto || selectedExp.iniciador || "")
                        : (selectedExp.caratula || "");
                    datos.descripcion = detalle
                        ? `${selectedExp.numero} - ${detalle}`
                        : selectedExp.numero || "";
                }
            }

            const data = await crearVinculadoJWT(idExpediente, selectedFile, datos);
            if (!data?.ok) throw new Error(data?.mensaje || "Error al guardar");
            setOpenAlta(false);
            setSnackbar({ open: true, message: "Documento vinculado agregado.", severity: "success" });
            fetchVinculados();
            if (onChanged) onChanged();
            resetForm();
        } catch (err) {
            setSnackbar({ open: true, message: err.message || "Error al guardar.", severity: "error" });
        } finally {
            setUploading(false);
        }
    };


    // Permisos de UI combinando prop legacy + nuevos permisos
    const uiPerm = useMemo(() => ({
        agregar: (permisos?.puedeAgregar !== false) && perm.insert,
        eliminar: (permisos?.puedeEliminar !== false) && perm.delete,
        ver: perm.select,
    }), [permisos, perm]);

    // Render
    return (
        <Box p={2} className={classes.root}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <TextField
                    variant="outlined"
                    placeholder="Buscar por descripción o tipo…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    size="small"
                    style={{ width: 360, maxWidth: "100%" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                {uiPerm.agregar && (
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        style={{
                            borderRadius: 4,
                            background: perm.insert ? "#1769aa" : undefined,
                            fontWeight: 600,
                            textTransform: "none",
                            marginBottom: 8,
                            color: "#fff",
                        }}
                        onClick={() => { setOpenAlta(true); resetForm(); }}
                    >
                        Vincular Documento
                    </Button>
                )}
            </Box>

            {/* Aviso sin permisos de lectura */}
            {perm.ready && !uiPerm.ver && (
                <Paper style={{ padding: 16, borderRadius: 4 }}>
                    <Typography color="text.secondary">
                        No tenés permisos para ver Vinculados (SELECT).
                    </Typography>
                </Paper>
            )}

            {perm.ready && uiPerm.ver && (
                <TableContainer component={Paper} className={classes.tableWrap}>
                    <Table size="small" stickyHeader>
                        <colgroup>
                            <col /><col style={{ width: 220 }} /><col style={{ width: 160 }} />{uiPerm.eliminar ? <col style={{ width: 90 }} /> : null}
                        </colgroup>

                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>Descripción</TableCell>
                                <TableCell className={classes.headCell}>Archivo</TableCell>
                                <TableCell className={classes.headCell}>Fecha/Hora</TableCell>
                                {uiPerm.eliminar && (
                                    <TableCell className={classes.headCell} align="center">Eliminar</TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {(loading ? [] : filtered)
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((v, idx) => {
                                    const ext = (v.extension || "").toLowerCase();
                                    const tipo = (v.tipo_archivo || "").toLowerCase();

                                    const go = async () => {
                                        const ext = (v.extension || "").toLowerCase();
                                        const tipo = (v.tipo_archivo || "").toLowerCase();
                                        const url = authFileUrl(idExpediente, v.id);

                                        // Expediente → link interno (formato "tipo:id" o solo "id")
                                        if (tipo === "expediente") {
                                            const raw = String(v.url_vinculado || "");
                                            let expTipo = "admin", expId = raw;
                                            if (raw.includes(":")) {
                                                const parts = raw.split(":");
                                                expTipo = parts[0];
                                                expId = parts[1];
                                            }
                                            window.open(`/expediente/${expTipo}/${expId}`, "_blank", "noopener,noreferrer");
                                            return;
                                        }

                                        // PDF → previsualizar en modal
                                        if (ext === "pdf") {
                                            const ok = await existsFile(fileUrlOf(v, idExpediente));
                                            if (!ok) { setNotFoundOpen(true); return; }
                                            setPreviewData({ url, extension: "pdf", vinculadoId: v.id, fileName: v.url_vinculado || v.descripcion });
                                            setOpenPreview(true);
                                            return;
                                        }

                                        // Imágenes → previsualizar en modal
                                        if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
                                            setPreviewData({ url, extension: ext, vinculadoId: v.id, fileName: v.url_vinculado || v.descripcion });
                                            setOpenPreview(true);
                                            return;
                                        }

                                        // Word/Excel/PPT/Otros → descargar con auth
                                        try {
                                            await downloadVinculadoJWT(idExpediente, v.id, v.url_vinculado || v.descripcion);
                                        } catch {
                                            setNotFoundOpen(true);
                                        }
                                    };


                                    return (
                                        <TableRow key={v.id} hover className={classes.rowClickable} onClick={go} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                            <TableCell>{v.descripcion}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={(() => {
                                                        if (tipo === "expediente") return <LinkIcon style={{ color: "#1567B7" }} />;
                                                        if (ext === "pdf") return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />;
                                                        if (["doc", "docx"].includes(ext)) return <DescriptionIcon style={{ color: "#1567B7" }} />;
                                                        if (["xls", "xlsx"].includes(ext)) return <TableChartIcon style={{ color: "#43a047" }} />;
                                                        if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <ImageIcon style={{ color: "#8e24aa" }} />;
                                                        return <InsertDriveFileIcon style={{ color: "#6B89AC" }} />;
                                                    })()}
                                                    label={getTipoLabel(v.tipo_archivo)}
                                                    onClick={(e) => { e.stopPropagation(); go(); }} // evita doble disparo
                                                    style={{ background: "#f5f7fb", fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>{formatDateTime24(v.fecha_alta)}</TableCell>
                                            {uiPerm.eliminar && (
                                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip title="Eliminar" arrow>
                                                        <span>
                                                            <IconButton
                                                                onClick={() => handleAskDelete(v.id)}
                                                                size="small"
                                                                style={{ color: "#F22E44" }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>

                    </Table>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filtered.length}   // 👈 antes estaba vinculados.length
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)} 
                        onRowsPerPageChange={(e) => {                          
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        style={filtered.length <= rowsPerPage ? { display: "none" } : undefined}
                    />
                </TableContainer>
            )}



            {/* MODAL: Online viewer Word/Excel/PDF */}
            <Dialog open={!!archivoViewer} onClose={() => setArchivoViewer(null)} maxWidth="md" fullWidth>
                <DialogTitle style={{ background: "#5B7894", color: "#fff" }}>Vista Previa de Archivo</DialogTitle>
                <DialogContent style={{ background: "#fff" }}>
                    {archivoViewer && <ArchivoViewer url={archivoViewer.url} ext={archivoViewer.ext} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchivoViewer(null)} color="primary">Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* MODAL: Alta/Adjuntar Vinculado */}
            <Dialog open={openAlta} onClose={() => { setOpenAlta(false); resetForm(); }} maxWidth="sm" fullWidth>
                <DialogTitle style={{ background: "#5B7894", color: "#fff" }}>Vincular Documento</DialogTitle>
                <DialogContent style={{ background: "#fff" }}>
                    <Box mt={2}>
                        <Autocomplete
                            options={tiposArchivo.filter(x => x.id > 0)}
                            getOptionLabel={option => getTipoLabel(option.tipo_archivo)}
                            value={tipoArchivo}
                            onChange={(_, value) => {
                                setTipoArchivo(value);
                                setSelectedFile(null);
                                setFilePreview(null);
                                setSelectedExp(null);
                            }}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={key} {...rest}>
                                        <Box display="flex" alignItems="center">
                                            {getIconByTipoArchivo(option.tipo_archivo, option.extension)}
                                            <span style={{ marginLeft: 10 }}>{getTipoLabel(option.tipo_archivo)}</span>
                                        </Box>
                                    </li>
                                );
                            }}
                            renderInput={params => <TextField {...params} label="Tipo de Archivo" variant="outlined" required />}
                            style={{ marginBottom: 14 }}
                        />

                        {/* Si es expediente, muestra filtro de tipo + autocomplete AJAX */}
                        {tipoArchivo && tipoArchivo.tipo_archivo?.toLowerCase() === "expediente" && (
                            <Box display="flex" gap={1} mb={1.5}>
                                {[
                                    { key: "todos", label: "Todos" },
                                    { key: "judicial", label: "Judicial" },
                                    { key: "admin", label: "Admin" },
                                ].map(opt => (
                                    <Chip
                                        key={opt.key}
                                        label={opt.label}
                                        onClick={() => { setExpTipoFiltro(opt.key); setSelectedExp(null); }}
                                        color={expTipoFiltro === opt.key ? "primary" : "default"}
                                        variant={expTipoFiltro === opt.key ? "filled" : "outlined"}
                                        style={{ fontWeight: 600 }}
                                    />
                                ))}
                            </Box>
                        )}
                        {tipoArchivo && tipoArchivo.tipo_archivo?.toLowerCase() === "expediente" && (
                            <Autocomplete
                                options={expedientes}
                                getOptionLabel={option => {
                                    const esAdmin = (option.tipo || "").toLowerCase().startsWith("admin");
                                    const detalle = esAdmin ? (option.extracto || option.iniciador || "") : (option.caratula || "");
                                    return detalle ? `${option.numero} - ${detalle} (${option.tipo})` : `${option.numero} (${option.tipo})`;
                                }}
                                loading={loadingExp}
                                value={selectedExp}
                                onInputChange={(_, value) => setExpBuscar(value)}
                                onChange={(_, value) => {
                                    setSelectedExp(value);
                                    if (value) {
                                        const esAdmin = (value.tipo || "").toLowerCase().startsWith("admin");
                                        const detalle = esAdmin
                                            ? (value.extracto || value.iniciador || "")
                                            : (value.caratula || "");
                                        setDescripcion(detalle ? `${value.numero} - ${detalle}` : value.numero || "");
                                    } else {
                                        setDescripcion("");
                                    }
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Buscar Expediente"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingExp ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                style={{ marginBottom: 14 }}
                            />
                        )}

                        {/* Si no es expediente, permite drag&drop y preview */}
                        {tipoArchivo && tipoArchivo.tipo_archivo?.toLowerCase() !== "expediente" && (() => {
                            const extPermitidas = extensionesPorTipo[tipoArchivo.tipo_archivo?.toLowerCase()] || [];
                            return (
                                <div {...getRootProps()} style={{
                                    border: "2px dashed #b0b7be",
                                    padding: "18px",
                                    textAlign: "center",
                                    background: isDragActive ? "#f0f7ff" : "#fafbfc",
                                    borderRadius: 4,
                                    marginBottom: 14,
                                    cursor: "pointer"
                                }}>
                                    <input
                                        {...getInputProps()}
                                        accept={extPermitidas.length ? extPermitidas.map(e => "." + e).join(",") : undefined}
                                        onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const ext = file.name.split(".").pop().toLowerCase();
                                                if (extPermitidas.length && !extPermitidas.includes(ext)) {
                                                    setSnackbar({
                                                        open: true,
                                                        message: `Formato inválido. Solo se permiten: ${extPermitidas.join(", ")}`,
                                                        severity: "error"
                                                    });
                                                    return;
                                                }
                                                setSelectedFile(file);
                                                if (file.type.startsWith("image/")) {
                                                    setFilePreview(URL.createObjectURL(file));
                                                } else if (file.type === "application/pdf") {
                                                    setFilePreview(URL.createObjectURL(file));
                                                } else {
                                                    setFilePreview(null);
                                                }
                                            }
                                        }}
                                    />
                                    {selectedFile ? (
                                        <Box>
                                            <Typography variant="body2" style={{ marginBottom: 10 }}>
                                                Archivo seleccionado: {selectedFile.name}
                                            </Typography>
                                            {filePreview && (
                                                tipoArchivo.tipo_archivo.toLowerCase() === "imagen"
                                                    ? <img
                                                        src={filePreview}
                                                        alt="Vista previa"
                                                        style={{ maxWidth: "100%", maxHeight: 540, display: "block", margin: "8px auto" }}
                                                    />
                                                    : tipoArchivo.tipo_archivo.toLowerCase() === "adobe pdf"
                                                        ? (
                                                            <object
                                                                data={filePreview}
                                                                type="application/pdf"
                                                                width="180"
                                                                height="130"
                                                                aria-label="Vista previa de PDF"
                                                            >
                                                                No se pudo mostrar la vista previa del PDF.
                                                            </object>
                                                        )
                                                        : null
                                            )}
                                        </Box>
                                    ) : (
                                        <Box color="#8a8a8a">
                                            <CloudUploadIcon fontSize="large" style={{ marginBottom: 4 }} />
                                            <div>
                                                Arrastrá el archivo aquí o hacé click para seleccionarlo.<br />
                                                {extPermitidas.length > 0 && (
                                                    <span style={{ fontSize: 13, color: "#1b3956" }}>
                                                        Tipos permitidos: {extPermitidas.map(e => "." + e).join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                        </Box>
                                    )}
                                </div>
                            );
                        })()}

                        <TextField
                            label="Descripción"
                            value={descripcion}
                            onChange={e => setDescripcion(e.target.value)}
                            variant="outlined"
                            fullWidth
                            required
                            style={{ marginBottom: 14 }}
                        />

                        {/* Checkbox para generar movimiento */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={generaMov}
                                    onChange={e => setGeneraMov(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Generar movimiento automáticamente"
                            style={{ marginTop: 8, marginBottom: 4 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => { setOpenAlta(false); resetForm(); }}
                        variant="outlined"
                        className={classes.btnDangerOutlined}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleGuardar}
                        variant="contained"
                        className={classes.btnPrimary}
                        disabled={
                            uploading ||
                            !perm.insert ||
                            !descripcion ||
                            !tipoArchivo ||
                            (tipoArchivo.tipo_archivo?.toLowerCase() === "expediente" && !selectedExp) ||
                            (tipoArchivo.tipo_archivo?.toLowerCase() !== "expediente" && !selectedFile)
                        }
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                        style={{ marginLeft: 10 }}
                    >
                        {uploading ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal vista previa */}
            <Dialog
                open={openPreview}
                onClose={closePreview}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle style={{ background: "#5B7894", color: "#fff", position: "relative" }}>
                Vista Previa de Archivo
                <span className={classes.titleActions}>
                    <Tooltip title="Abrir en pestaña">
                        <IconButton
                            onClick={() => previewData?.url && window.open(previewData.url, "_blank", "noopener")}
                            size="small"
                            style={{ color: "#fff" }}
                        >
                            <LinkIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar">
                        <IconButton
                            onClick={() => {
                                if (!previewData?.vinculadoId) return;
                                downloadVinculadoJWT(idExpediente, previewData.vinculadoId, previewData.fileName || "archivo");
                            }}
                            size="small"
                            style={{ color: "#fff" }}
                        >
                            <CloudUploadIcon style={{ transform: "rotate(180deg)" }} />
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={closePreview} size="small" style={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </span>
            </DialogTitle>

            <DialogContent style={{ background: "#fafbfc", minHeight: 450 }}>
                    {String(previewData?.extension).toLowerCase() === "pdf" ? (
                        <iframe
                            src={previewData.url}
                            width="100%"
                            height="540px"
                            title="Visor PDF"
                            style={{ border: "none" }}
                        />
                    ) : (
                        <img
                            src={previewData?.url}
                            alt="Vista previa"
                            style={{ maxWidth: "100%", maxHeight: 540, display: "block", margin: "8px auto" }}
                            onError={() => { setOpenPreview(false); setNotFoundOpen(true); }}
                        />
                    )}
            </DialogContent>
        </Dialog>


            {/* MODAL: Confirmación borrado */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle style={{ background: "#6B89AC", color: "#fff" }}>
                    Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" style={{ margin: "12px 0 8px" }}>
                        ¿Seguro que desea eliminar este documento vinculado?
                    </Typography>
                    <Box mt={1} mb={1} p={12 / 10} style={{ background: "#f8fafc", borderRadius: 4 }}>
                        <Typography variant="body2"><b>Descripción:</b> {rowToDelete?.descripcion || "-"}</Typography>
                        <Typography variant="body2"><b>Tipo:</b> {getTipoLabel(rowToDelete?.tipo_archivo)}</Typography>
                        <Typography variant="body2"><b>Fecha/Hora:</b> {formatDateTime24(rowToDelete?.fecha_alta)}</Typography>
                        <Typography variant="caption" style={{ color: "#c62828", display: "block", marginTop: 6 }}>
                            Esta acción no se puede deshacer. Se registrará en el historial.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenConfirm(false)}
                        variant="outlined"
                        className={classes.btnDangerOutlined}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        className={classes.btnPrimary}
                        style={{ marginLeft: 12 }}
                        disabled={!perm.delete}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* MODAL: Archivo no disponible */}
            <Dialog open={notFoundOpen} onClose={() => setNotFoundOpen(false)}>
                <DialogTitle style={{ background: "#6B89AC", color: "#fff" }}>
                    Archivo no disponible
                </DialogTitle>
                <DialogContent>
                    <Typography>El archivo no existe o fue eliminado.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotFoundOpen(false)} variant="contained" className={classes.btnPrimary}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                <AlertSnackbar severity={snackbar.severity}>{snackbar.message}</AlertSnackbar>
            </Snackbar>
        </Box>
    );
}
